import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import SignOutButton from '../../components/SignOutButton';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  ClipboardList,
  ClipboardCheck,
  Gauge,
  Layers,
  Wrench,
  Award,
  Bell,
  DollarSign,
  UserCircle,
  CalendarDays,
  ChevronRight,
  ArrowLeft,
  History,
  ReceiptText,
  Moon,
  Sun,
  Shield,
} from 'lucide-react';
import { useBack } from '../../hooks/useBack';

export default function FieldMoreScreen() {
  const navigate = useNavigate();
  const goBack = useBack('/field');
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const { hasVertical } = useAuth();

  const menuItems = [
    // A downhole_tools tenant's way into /field/downhole, matching the tile on
    // the home screen. A positive guard: no other tenant sees the row.
    ...(hasVertical('downhole_tools')
      ? [{ title: 'Downhole tools', subtitle: 'Assemblies, runs, teardown and your work orders', icon: Layers, path: '/field/downhole', color: 'text-fieldiq-accent' }]
      : []),
    // A pressure_testing tenant's way into /field/pt (PT-3), matching the entry on Home.
    ...(hasVertical('pressure_testing')
      ? [{ title: 'My PT Jobs', subtitle: 'Your test jobs, holds and chart photos', icon: Gauge, path: '/field/pt', color: 'text-fieldiq-accent' }]
      : []),
    // An ndt_inspection tenant's way into /field/ndt (fieldiq-api#1144), matching the entry on Home.
    ...(hasVertical('ndt_inspection')
      ? [{ title: 'My NDT Jobs', subtitle: 'Your crewed jobs, welds and exams', icon: ClipboardCheck, path: '/field/ndt', color: 'text-fieldiq-accent' }]
      : []),
    // A pipeline_facility tenant's way into /field/pf (fieldiq-api#1200), matching the entry on Home.
    ...(hasVertical('pipeline_facility')
      ? [
          { title: 'My PF Jobs', subtitle: 'Your crewed jobs and daily logs', icon: ClipboardList, path: '/field/pf', color: 'text-fieldiq-accent' },
          // PF-3a (fieldiq-api#1219), matching the entry on Home.
          { title: 'My PF Digs', subtitle: 'Features found in the ditch, and the dig steps', icon: Layers, path: '/field/pf/digs', color: 'text-fieldiq-accent' },
        ]
      : []),
    // A well_testing tenant's way into /field/wt (fieldiq-api#1245, WT-2), matching the entry on Home.
    ...(hasVertical('well_testing')
      ? [{ title: 'My WT Jobs', subtitle: 'Your crewed jobs and day / night shift logs', icon: ClipboardList, path: '/field/wt', color: 'text-fieldiq-accent' }]
      : []),
    // A well_servicing tenant's way into /field/ws (fieldiq-api#1282, WS-2), matching the entry on Home.
    ...(hasVertical('well_servicing')
      ? [
          { title: 'My Tours', subtitle: 'Your rig jobs and daily tour sheets', icon: ClipboardList, path: '/field/ws', color: 'text-fieldiq-accent' },
          // The shared BOP register, read only for a hand, matching the entry on Home.
          { title: 'Pressure Control', subtitle: 'BOP certificates, stack, tests and drills', icon: Shield, path: '/equipment/pressure-control', color: 'text-fieldiq-accent' },
        ]
      : []),
    // A wireline_slickline tenant's way into /field/wl (fieldiq-api#1338, WL-2), matching the entry on Home.
    ...(hasVertical('wireline_slickline')
      ? [{ title: 'My Sheets', subtitle: 'Your unit jobs, day sheets and runs', icon: ClipboardList, path: '/field/wl', color: 'text-fieldiq-accent' }]
      : []),
    // An earthworks tenant's way into /field/ew (fieldiq-api#1284, EW-4), matching the entry on Home.
    ...(hasVertical('earthworks')
      ? [{ title: 'My EW Jobs', subtitle: 'Your crewed jobs, daily logs, loads and dump tickets', icon: ClipboardList, path: '/field/ew', color: 'text-fieldiq-accent' }]
      : []),
    { title: 'Forms', subtitle: 'Open a safety form or inspection', icon: ClipboardList, path: '/field/forms', color: 'text-sky-400' },
    // Directly under Forms: that entry is where an inspection is STARTED, this
    // one is where it is found again afterwards. A route with no nav entry is
    // invisible, and this screen and the Safety tile are the only two doors.
    // Casing's own (PT-3): it reads /casing/inspection-submissions, which
    // answers a tenant without casing_running with a 403. Same positive guard
    // as the tile on Home.
    ...(hasVertical('casing_running')
      ? [{ title: 'My Inspections', subtitle: 'Inspections you filed, and unfinished ones', icon: History, path: '/field/safety/inspections', color: 'text-sky-400' }]
      : []),
    { title: 'Equipment', subtitle: 'View assigned equipment & run checks', icon: Wrench, path: '/field/equipment', color: 'text-fieldiq-accent' },
    { title: 'Certifications', subtitle: 'View your certifications & expiry dates', icon: Award, path: '/field/certs', color: 'text-green-400' },
    { title: 'Time Off', subtitle: 'Request & view time off', icon: CalendarDays, path: '/field/time-off', color: 'text-cyan-400' },
    { title: 'Pay', subtitle: 'View pay periods & earnings', icon: DollarSign, path: '/field/pay', color: 'text-amber-400' },
    { title: 'Expenses', subtitle: 'Photograph a receipt & track the claim', icon: ReceiptText, path: '/field/expenses', color: 'text-emerald-400' },
    { title: 'Notifications', subtitle: 'Choose how you get notified', icon: Bell, path: '/field/settings/notifications', color: 'text-purple-400' },
    { title: 'Profile', subtitle: 'Edit your info & change password', icon: UserCircle, path: '/field/profile', color: 'text-blue-400' },
  ];

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="max-w-[1920px] mx-auto">
        <Header />
        <div className="px-4 py-3 border-b border-fieldiq-border flex items-center gap-3">
          <button onClick={goBack} aria-label="Back" className="text-fieldiq-text">
            <ArrowLeft size={26} />
          </button>
          <h1 className="text-xl font-bold text-white">More</h1>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4 space-y-3">
        {menuItems.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className="w-full bg-fieldiq-card rounded-xl p-4 flex items-center justify-between card-hover"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-fieldiq-dark/50 flex items-center justify-center">
                  <Icon size={20} className={item.color} />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold">{item.title}</h3>
                  <p className="text-xs text-fieldiq-text">{item.subtitle}</p>
                </div>
              </div>
              <ChevronRight size={20} className="text-fieldiq-muted" />
            </button>
          );
        })}

        {/* The phone header has no theme toggle (it gave its width to the company
          * name), and a field hand's gear opens this screen, so the toggle lives
          * here. It is the only theme control a field hand has on a phone. */}
        <button
          onClick={toggleTheme}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          className="w-full bg-fieldiq-card rounded-xl p-4 flex items-center justify-between card-hover"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-fieldiq-dark/50 flex items-center justify-center">
              {isDark ? <Moon size={20} className="text-fieldiq-accent" /> : <Sun size={20} className="text-fieldiq-accent" />}
            </div>
            <div className="text-left">
              <h3 className="font-semibold">Theme</h3>
              <p className="text-xs text-fieldiq-text">{isDark ? 'Dark — tap for light' : 'Light — tap for dark'}</p>
            </div>
          </div>
        </button>

        <SignOutButton />
      </div>
    </div>
  );
}
